import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Col, DatePicker, Empty, Form, Input, Row, Select, Space, Table, Tag } from 'antd';
import CBInputNumber from '../../components/CBInputNumber';
import { PlusOutlined, ReloadOutlined, SearchOutlined, ClearOutlined, BankOutlined } from '@ant-design/icons';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { formatDate, formatMontant, formatNumber, STATUTS_CHEQUE } from '../../variables/constants';
import { toast } from 'react-toastify';
import CBModal from '../../components/CBModal';
import TableExportButton from '../../components/TableExportButton';
import KpiCards from '../../components/KpiCards';
import logoLocal from '../../assets/images/logo.png';
import PageHeader from '../../components/PageHeader';
import SelectBanque from '../../components/SelectBanque';

import { useTranslation } from 'react-i18next';
import { localeIntl } from '../../i18n/format';

const { Option } = Select;

const STATUT_CHEQUE_MAP = STATUTS_CHEQUE.reduce((acc, s) => (acc[s.value] = s, acc), {});

export default function Encaissements() {
  const { t } = useTranslation('encaissements');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  // Recherche serveur du sinistre (le champ de saisie interroge la base, pas une liste figée).
  const [sinistreOpts, setSinistreOpts] = useState([]);
  const [sinistreFetching, setSinistreFetching] = useState(false);
  const recentSinistresRef = useRef([]);          // sinistres récents affichés par défaut
  const sinistreFetchRef = useRef({ timer: null, id: 0 });
  const sinistreMapRef = useRef({});              // trackingId -> objet sinistre complet (pour le pré-remplissage)
  const [sinistreSel, setSinistreSel] = useState(null); // sinistre sélectionné (bandeau de contexte)

  // Debounce (350 ms) + min 2 caractères + protection anti-race : seule la réponse de la
  // requête la plus récente est appliquée (les réponses périmées sont ignorées).
  const chercherSinistres = useCallback((q) => {
    const ctx = sinistreFetchRef.current;
    if (ctx.timer) clearTimeout(ctx.timer);
    const terme = (q || '').trim();
    if (terme.length < 2) {
      setSinistreFetching(false);
      setSinistreOpts(recentSinistresRef.current);
      return;
    }
    setSinistreFetching(true);
    ctx.timer = setTimeout(async () => {
      const monId = ++ctx.id;
      try {
        const res = await axiosInstance.get('/v1/sinistres', { params: { search: terme, page: 0, size: 20 } });
        if (monId !== ctx.id) return; // réponse périmée
        // PaginatedResponse : data est la liste directe (pas data.content).
        const content = res.data?.data || [];
        content.forEach(s => { sinistreMapRef.current[s.sinistreTrackingId] = s; });
        setSinistreOpts(content.map(s => ({ value: s.sinistreTrackingId, label: s.numeroSinistreLocal })));
      } catch {
        if (monId === ctx.id) setSinistreOpts([]);
      } finally {
        if (monId === ctx.id) setSinistreFetching(false);
      }
    }, 350);
  }, []);
  const [organismes, setOrganismes] = useState([]);
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();

  // Sélection d'un sinistre : bandeau de contexte + pré-remplissage
  //  - Organisme émetteur  ← compagnie membre du sinistre (émetteur habituel du chèque)
  //  - Chèque à l'ordre de ← bureau national (bénéficiaire habituel)
  const onSelectSinistre = useCallback((trackingId) => {
    const s = trackingId ? (sinistreMapRef.current[trackingId] || null) : null;
    setSinistreSel(s);
    if (!s) return;
    const patch = {};
    if (s.organismeMembreTrackingId) patch.organismeTrackingId = s.organismeMembreTrackingId;
    const bureauNational = organismes.find(o => o.typeOrganisme === 'BUREAU_NATIONAL');
    if (bureauNational) patch.chequeOrdreOrganismeTrackingId = bureauNational.organismeTrackingId;
    if (Object.keys(patch).length) form.setFieldsValue(patch);
  }, [organismes, form]);

  // Réinitialise le bandeau de contexte à la fermeture du modal (toutes voies confondues).
  useEffect(() => { if (!modalOpen) setSinistreSel(null); }, [modalOpen]);

  /* Taux des frais de gestion, lu dans le paramètre PCT_FRAIS_GESTION — celui que le
     serveur applique réellement (TauxFraisGestion). Il était écrit « 5 % » et « × 0.05 »
     ici : changer le taux dans Paramètres déplaçait la retenue comptabilisée sans
     déplacer ni le libellé ni l'estimation, et l'agent validait un montant que le
     serveur n'allait pas retenir. Repli à 5, la valeur de repli du serveur, si la
     lecture échoue — l'estimation reste indicative, elle ne doit pas bloquer la saisie. */
  const [pctFraisGestion, setPctFraisGestion] = useState(5);
  useEffect(() => {
    let annule = false;
    axiosInstance.get('/v1/delais/parametres-systeme', { skipErrorToast: true })
      .then((r) => {
        const p = (r.data?.data || []).find((x) => x.cle === 'PCT_FRAIS_GESTION' && x.actif);
        if (!annule && p?.valeurDecimal != null) setPctFraisGestion(Number(p.valeurDecimal));
      })
      .catch(() => { /* repli conservé */ });
    return () => { annule = true; };
  }, []);

  const fetchAll = useCallback(async () => {
    const hasFilter = Object.values(filters).some(v => v !== undefined && v !== null && v !== '');
    const url = hasFilter ? '/v1/encaissements/search' : '/v1/encaissements';
    const SIZE = 200;
    const first = await axiosInstance.get(url, { params: { ...(hasFilter ? filters : {}), page: 0, size: SIZE } });
    const all = [...(first.data?.data || [])];
    const totalPages = first.data?.totalPages ?? 1;
    if (totalPages > 1) {
      const rest = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, i) => i + 1).map(p =>
          axiosInstance.get(url, { params: { ...(hasFilter ? filters : {}), page: p, size: SIZE } })
        )
      );
      rest.forEach(r => all.push(...(r.data?.data || [])));
    }
    return all;
  }, [filters]);

  const load = useCallback(async (page = 0, size = 20, criteria = {}) => {
    setLoading(true);
    try {
      const hasFilter = Object.values(criteria).some(v => v !== undefined && v !== null && v !== '');
      const url = hasFilter ? '/v1/encaissements/search' : '/v1/encaissements';
      const params = { page, size, ...(hasFilter ? criteria : {}) };
      const res = await axiosInstance.get(url, { params });
      const body = res.data || {};
      setItems(body.data || []);
      setPagination({
        current: (body.currentPage ?? 0) + 1,
        pageSize: body.pageSize ?? size,
        total: body.totalElements ?? 0,
      });
    } catch {
      setItems([]);
      setPagination(p => ({ ...p, total: 0 }));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRefs = useCallback(async () => {
    try {
      const sRes = await axiosInstance.get('/v1/sinistres?page=0&size=50');
      const content = sRes.data?.data || [];
      content.forEach(s => { sinistreMapRef.current[s.sinistreTrackingId] = s; });
      const opts = content.map(s => ({ value: s.sinistreTrackingId, label: s.numeroSinistreLocal }));
      recentSinistresRef.current = opts;
      setSinistreOpts(opts);
    } catch { recentSinistresRef.current = []; setSinistreOpts([]); }
    try {
      const oRes = await axiosInstance.get('/v1/organismes');
      setOrganismes(oRes.data?.data || []);
    } catch { setOrganismes([]); }
  }, []);

  useEffect(() => {
    loadRefs();
    load(0, 20, {});
  }, [load, loadRefs]);

  //  KPI aggregates calculés sur les items chargés 
  // pagination.total = compteur global exact (toutes pages).
  // Les montants / statuts reflètent la page / vue courante.
  const kpiItems = useMemo(() => {
    const nbEncaisse = items.filter(i => i.statutCheque === 'ENCAISSE').length;
    const nbAnnule = items.filter(i => i.statutCheque === 'ANNULE').length;
    const nbEnAttente = items.length - nbEncaisse - nbAnnule;

    const montantTotal = items.reduce((s, i) => s + (Number(i.montantCheque) || 0), 0);
    const fraisTotal = items.reduce((s, i) => s + (Number(i.produitFraisGestion) || 0), 0);

    const isFiltered = Object.values(filters).some(v => v !== undefined && v !== null && v !== '');
    const totalLabel = isFiltered
      ? t('kpi.resultatsFiltres', { count: pagination.total, n: pagination.total.toLocaleString(localeIntl()) }) : t('kpi.auTotal', { n: pagination.total.toLocaleString(localeIntl()) });

    return [
      {
        label: t('kpi.total'),
        value: pagination.total.toLocaleString(localeIntl()),
        color: '#374151',
        bg: '#f3f4f6',
        suffix: totalLabel,
      },
      {
        label: t('kpi.montantVue'),
        value: formatNumber(montantTotal),
        color: '#1a6b3c',
        bg: '#f0fdf4',
      },
      {
        label: t('kpi.fraisGestion'),
        value: formatNumber(fraisTotal),
        color: '#2563eb',
        bg: '#eff6ff',
      },
    ];
  }, [items, pagination.total, filters, t]);

  const onTableChange = (pag) => load(pag.current - 1, pag.pageSize, filters);

  const onSearch = (vals) => {
    const criteria = {
      numeroCheque: vals.numeroCheque || undefined,
      banqueEmettrice: vals.banqueEmettrice || undefined,
      organismeEmetteurId: vals.organismeEmetteurId || undefined,
      numeroSinistreLocal: vals.numeroSinistreLocal || undefined,
      montantMin: vals.montantMin ?? undefined,
      montantMax: vals.montantMax ?? undefined,
    };
    setFilters(criteria);
    load(0, pagination.pageSize, criteria);
  };

  const onReset = () => {
    searchForm.resetFields();
    setFilters({});
    load(0, pagination.pageSize, {});
  };

  const creer = async (vals) => {
    try {
      await axiosInstance.post('/v1/encaissements', {
        numeroCheque: vals.numeroCheque,
        montantCheque: vals.montantCheque,
        montantTheorique: vals.montantTheorique || vals.montantCheque,
        appliquerFraisGestion: vals.appliquerFraisGestion,
        dateEmission: vals.dateEmission?.format('YYYY-MM-DD'),
        dateReception: vals.dateReception?.format('YYYY-MM-DD'),
        banqueEmettrice: vals.banqueEmettrice,
        organismeEmetteurTrackingId: vals.organismeTrackingId,
        chequeOrdreOrganismeTrackingId: vals.chequeOrdreOrganismeTrackingId,
        sinistreTrackingId: vals.sinistreTrackingId,
      });
      toast.success(t('messages.enregistre'));
      form.resetFields();
      setModalOpen(false);
      load(0, pagination.pageSize, filters);
    } catch {
      toast.error(t('messages.erreur'));
    }
  };

  const cols = [
    {
      title: t('colonnes.numeroCheque'), dataIndex: 'numeroCheque', key: 'num', width: 160,
      render: v => <span style={{ fontFamily: 'var(--font-main)', fontWeight: 600 }}>{v}</span>,
    },
    { title: t('colonnes.organismeEmetteur'), dataIndex: 'organismeEmetteurRaisonSociale', key: 'org', ellipsis: true },
    {
      title: t('colonnes.ordre'), dataIndex: 'chequeOrdreOrganismeNom', key: 'ord', ellipsis: true,
      render: v => v || '-',
    },
    {
      title: t('colonnes.sinistre'), dataIndex: 'sinistreNumeroLocal', key: 'sin', width: 160,
      render: v => <span style={{ fontWeight: 600, color: '#1a6b3c' }}>{v}</span>,
    },
    {
      title: t('colonnes.montant'), dataIndex: 'montantCheque', key: 'mt', width: 140, align: 'right',
      format: 'currency', numeric: true,
      render: v => <span style={{ fontWeight: 600 }}>{formatMontant(v)}</span>,
    },
    {
      title: t('colonnes.theorique'), dataIndex: 'montantTheorique', key: 'th', width: 130, align: 'right',
      format: 'currency', numeric: true,
      render: v => formatMontant(v),
    },
    {
      title: t('colonnes.fraisGestion'), dataIndex: 'produitFraisGestion', key: 'fg', width: 130, align: 'right',
      format: 'currency', numeric: true,
      render: v => v
        ? <span style={{ color: '#1a6b3c', fontWeight: 600 }}>{formatMontant(v)}</span>
        : '-',
    },
    {
      title: t('colonnes.banque'), dataIndex: 'banqueEmettrice', key: 'bq', width: 150, ellipsis: true,
      render: v => v || '-',
    },
    {
      title: t('colonnes.reception'), dataIndex: 'dateReception', key: 'dr', width: 110, format: 'date',
      render: v => v ? formatDate(v) : '-',
    },
    {
      title: t('colonnes.encaissement'), dataIndex: 'dateEncaissement', key: 'de', width: 120, format: 'date',
      render: v => v ? formatDate(v) : '-',
    },
    {
      title: t('colonnes.statut'), dataIndex: 'statutCheque', key: 'st', width: 110,
      export: v => STATUT_CHEQUE_MAP[v]?.label || v || '',
      render: v => {
        const s = STATUT_CHEQUE_MAP[v] || { label: v, color: 'default' };
        return <Tag color={s.color} style={{ borderRadius: 20 }}>{s.label}</Tag>;
      },
    },
  ];

  return (
    <Layout title={t('titre')}>
      {/* `pagination.total` : compteur global exact toutes pages confondues, deja
          utilise par les KPI de l'ecran - `items` ne contient que la page courante. */}
      <PageHeader
        titre={t('titre')}
        icone={<BankOutlined />}
        compte={pagination.total}
        unite={t('unite')}
        unitePluriel={t('unitePluriel')}
        actions={(
          <>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => load(pagination.current - 1, pagination.pageSize, filters)}
              style={{ borderRadius: 8 }}
            />
            <Button
              type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}
              style={{ background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 8 }}
            >
              {t('enregistrerCheque')}
            </Button>
          </>
        )}
      />

      {/*  KPI cards  */}
      <KpiCards items={kpiItems} columns={4} />

      {/*  Filtres  */}
      <div className="cb-card" style={{ padding: 16, marginBottom: 16 }}>
        <Form form={searchForm} layout="vertical" onFinish={onSearch}>
          <Row gutter={12}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="numeroCheque" label={t('filtres.numeroCheque')} style={{ marginBottom: 8 }}>
                <Input allowClear placeholder={t('filtres.numeroChequeExemple')} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="banqueEmettrice" label={t('filtres.banque')} style={{ marginBottom: 8 }}>
                <Input allowClear placeholder={t('filtres.banqueExemple')} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="organismeEmetteurId" label={t('filtres.organismeEmetteur')} style={{ marginBottom: 8 }}>
                <Select allowClear showSearch placeholder={t('filtres.compagnieBureau')}
                  filterOption={(i, o) => (o.children || '').toLowerCase().includes(i.toLowerCase())}>
                  {organismes.map(o => (
                    <Option key={o.organismeTrackingId} value={o.organismeTrackingId}>{o.raisonSociale}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="numeroSinistreLocal" label={t('filtres.numeroSinistre')} style={{ marginBottom: 8 }}>
                <Input allowClear placeholder={t('filtres.numeroSinistreExemple')} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="montantMin" label={t('filtres.montantMin')} style={{ marginBottom: 8 }}>
                <CBInputNumber style={{ width: '100%' }} min={0} step={1000}
                  formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="montantMax" label={t('filtres.montantMax')} style={{ marginBottom: 8 }}>
                <CBInputNumber style={{ width: '100%' }} min={0} step={1000}
                  formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} style={{ display: 'flex', alignItems: 'flex-end' }}>
              <Space>
                <Button type="primary" htmlType="submit" icon={<SearchOutlined />}
                  style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}>
                  {t('filtres.rechercher')}
                </Button>
                <Button icon={<ClearOutlined />} onClick={onReset}>{t('filtres.reinitialiser')}</Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </div>

      {/*  Tableau  */}
      <div className="cb-card" style={{ padding: 0 }}>
        <Table
          className="cb-table"
          dataSource={items}
          columns={cols}
          rowKey="encaissementTrackingId"
          loading={loading}
          scroll={{ x: 1400 }}
          onChange={onTableChange}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) =>
              t('tableau.pagination', { count: total, debut: range[0], fin: range[1] }),
          }}
          locale={{
            emptyText: (
              <Empty
                description={t('tableau.aucun')}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
        />
      </div>

      {/*  Export flottant  */}
      {/* Le positionnement flottant vient de `floating`, qui pose lui-meme
          l'enveloppe et reserve la place en bas de page. L'enveloppe ecrite a la
          main qui etait ici la doublait a 24 px au lieu de 32 : deux coins pour
          un seul bouton, et le second gagnait. */}
      <TableExportButton
        dataLoader={fetchAll}
        columns={cols}
        filename="encaissements"
        title={t('titre')}
        subtitle={t(Object.values(filters).some(v => v !== undefined && v !== null && v !== '') ? 'export.sousTitreFiltre' : 'export.sousTitreTous', { count: pagination.total })}
        backgroundColor="#1a6b3c"
        background="#1a6b3c"
        color="#ffffff"
        floating="true"
        logo={logoLocal}
        count={pagination.total}
        loadingLabel={t('export.preparation')}
      />

      {/*  Modal création  */}
      <CBModal
        title={t('modale.titre')}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={creer}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="sinistreTrackingId" label={t('modale.sinistre')} rules={[{ required: true }]}>
                <Select
                  showSearch
                  allowClear
                  filterOption={false}
                  onSearch={chercherSinistres}
                  onChange={onSelectSinistre}
                  loading={sinistreFetching}
                  placeholder={t('modale.rechercherSinistre')}
                  notFoundContent={sinistreFetching ? t('modale.recherche') : t('modale.deuxCaracteres')}
                  options={sinistreOpts}
                />
              </Form.Item>
            </Col>

            {sinistreSel && (
              <Col span={24}>
                <div style={{
                  marginTop: -4, marginBottom: 12, padding: '8px 12px',
                  background: '#f0fdf4', border: '1px solid #d1fae5', borderRadius: 8,
                  fontSize: 12, lineHeight: 1.7,
                }}>
                  <Space size={16} wrap>
                    <span><strong>{t('modale.assure')}</strong> {sinistreSel.assureNomComplet || '-'}
                      {sinistreSel.assureImmatriculation ? ` · ${sinistreSel.assureImmatriculation}` : ''}</span>
                    <span><strong>{t('modale.compagnie')}</strong> {sinistreSel.organismeMembreRaisonSociale || '-'}</span>
                  </Space>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 4 }}>
                    <Tag style={{ margin: 0 }}>{sinistreSel.statut}</Tag>
                    {sinistreSel.tauxRc != null && <Tag color="green" style={{ margin: 0 }}>{t('modale.tauxRc', { taux: sinistreSel.tauxRc })}</Tag>}
                    {sinistreSel.paysEmetteurLibelle && <Tag style={{ margin: 0 }}>{t('modale.emetteur', { pays: sinistreSel.paysEmetteurLibelle })}</Tag>}
                    {sinistreSel.estPrefinance && <Tag color="orange" style={{ margin: 0 }}>{t('modale.prefinance')}</Tag>}
                    {sinistreSel.estContentieux && <Tag color="red" style={{ margin: 0 }}>{t('modale.contentieux')}</Tag>}
                  </div>
                </div>
              </Col>
            )}
            <Col span={24}>
              <Form.Item name="organismeTrackingId" label={t('filtres.organismeEmetteur')} rules={[{ required: true }]}>
                <Select
                  showSearch
                  placeholder={t('filtres.compagnieBureau')}
                  filterOption={(i, o) => (o.children || '').toLowerCase().includes(i.toLowerCase())}
                  popupMatchSelectWidth={false}
                >
                  {organismes.map(o => (
                    <Option key={o.organismeTrackingId} value={o.organismeTrackingId}>{o.raisonSociale}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="chequeOrdreOrganismeTrackingId" label={t('modale.ordre')} rules={[{ required: true }]}>
                <Select
                  showSearch
                  placeholder={t('modale.beneficiaire')}
                  filterOption={(i, o) => (o.children || '').toLowerCase().includes(i.toLowerCase())}
                  popupMatchSelectWidth={false}
                >
                  {organismes.map(o => (
                    <Option key={o.organismeTrackingId} value={o.organismeTrackingId}>{o.raisonSociale}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="numeroCheque" label={t('filtres.numeroCheque')} rules={[{ required: true }]}>
                <Input placeholder={t('modale.numeroChequeExemple')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="banqueEmettrice" label={t('modale.banqueEmettrice')}>
                <SelectBanque placeholder={t('modale.banqueEmettriceCheque')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="montantCheque" label={t('modale.montantCheque')} rules={[{ required: true }]}>
                <CBInputNumber style={{ width: '100%' }} min={0} step={1000}
                  formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="montantTheorique" label={t('modale.montantTheorique')}>
                <CBInputNumber style={{ width: '100%' }} min={0} step={1000}
                  formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} />
              </Form.Item>
            </Col>
            {/* Frais de gestion : la case décide, le montant se calcule sur le
                THÉORIQUE - ce qui était dû - et non sur le chèque - ce qui a été
                reçu. Les asseoir sur le chèque faisait varier la commission du
                bureau avec une erreur de l'émetteur. */}
            <Col span={12}>
              <Form.Item name="appliquerFraisGestion" valuePropName="checked" style={{ marginBottom: 4 }}>
                <Checkbox>{t('modale.appliquerFrais', { pct: pctFraisGestion.toLocaleString(localeIntl()) })}</Checkbox>
              </Form.Item>
              <Form.Item
                dependencies={['montantTheorique', 'montantCheque', 'appliquerFraisGestion']}
                noStyle>
                {({ getFieldValue }) => {
                  if (!getFieldValue('appliquerFraisGestion')) return null;
                  const theorique = Number(getFieldValue('montantTheorique') || 0);
                  const cheque = Number(getFieldValue('montantCheque') || 0);
                  const assiette = theorique > 0 ? theorique : cheque;
                  return (
                    <div style={{ fontSize: 12.5, color: '#6b7280' }}>
                      {t('modale.retenueEstimee')} <strong>{Math.round(assiette * pctFraisGestion / 100).toLocaleString(localeIntl())}</strong> FCFA
                    </div>
                  );
                }}
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="dateEmission" label={t('modale.dateEmission')} rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="dateReception" label={t('modale.dateReception')}>
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
          </Row>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
            <Button onClick={() => setModalOpen(false)}>{t('modale.annuler')}</Button>
            <Button type="primary" htmlType="submit"
              style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}>
              {t('modale.enregistrer')}
            </Button>
          </div>
        </Form>
      </CBModal>
    </Layout>
  );
}